// Typed fetch wrappers for the FastAPI backend.
// All endpoints are same-origin in production and proxied in dev (vite.config.ts).

export interface User {
  id: number;
  email: string;
  name: string | null;
  google_sub: string | null;
  created_at: number;
  last_login_at: number | null;
}

export interface AttributionUser {
  id: number;
  email: string;
  name: string | null;
}

export interface AttributedRecord {
  created_by: AttributionUser | null;
}

export interface Thread extends AttributedRecord {
  id: number;
  user_id: number;
  title: string;
  created_at: number;
  updated_at: number;
  archived: boolean;
  project_id: number | null;
}

export interface Project extends AttributedRecord {
  id: number;
  user_id: number;
  name: string;
  color: string | null;
  archived: boolean;
  sort_order: number;
  created_at: number;
  updated_at: number;
  counts: { chat: number; task: number; research: number; doc: number; total: number };
}

export interface Tag {
  id: number;
  user_id: number;
  name: string;
  color: string | null;
  created_at: number;
  item_count: number;
}

export type ItemKind = "chat" | "task" | "research" | "doc" | "library";

export interface Citation {
  source_id: number;
  chunk_id?: number | null;
  title?: string | null;
  authors?: string | null;
  year?: number | null;
  url?: string | null;
  abby_private?: boolean;
}

export interface Message {
  id: number;
  thread_id: number;
  role: "user" | "assistant" | "system";
  content: string;
  citations: Citation[];
  token_usage: { input_tokens?: number; output_tokens?: number; cache_read_input_tokens?: number } | null;
  created_at: number;
}

export interface LibrarySource extends AttributedRecord {
  id: number;
  title: string;
  authors: string | null;
  year: number | null;
  url: string | null;
  source_category: string;
  license_note: string | null;
  ingestion_method: string;
  abby_private: number;
  added_at: number;
  chunk_count: number;
  status: string;
  byte_size: number;
  mime: string | null;
  tags: { id: number; name: string; color: string | null }[];
}

export interface LibraryCategory {
  id: number;
  key: string;
  label: string;
  sort_order: number;
  archived: boolean;
  created_at: number;
  updated_at: number;
  source_count: number;
}

export interface TaskTemplate {
  key: string;
  display_name: string;
  short_description: string;
  inputs: TaskTemplateField[];
}

export interface TaskTemplateField {
  name: string;
  label: string;
  type: "text" | "textarea" | "select";
  default?: string;
  options?: string[];
  optional?: boolean;
}

export interface ResearchCandidate {
  title: string;
  authors?: string | null;
  year?: number | null;
  url: string;
  abstract?: string | null;
  why_relevant?: string | null;
  license_likelihood?: string | null;
}

class ApiError extends Error {
  status: number;
  constructor(status: number, message: string) {
    super(message);
    this.status = status;
  }
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const resp = await fetch(path, {
    credentials: "include",
    ...init,
    headers: {
      "Content-Type": "application/json",
      ...(init?.headers ?? {}),
    },
  });
  if (!resp.ok) {
    let detail = resp.statusText;
    try {
      const body = await resp.json();
      detail = body?.detail ?? detail;
    } catch {
      /* ignore */
    }
    throw new ApiError(resp.status, detail);
  }
  if (resp.status === 204) return undefined as T;
  return resp.json();
}

export const api = {
  // ---- Auth ----
  me: () => request<User>("/auth/me"),
  loginUrl: (next?: string) =>
    `/auth/google/start${next ? `?next=${encodeURIComponent(next)}` : ""}`,
  devLoginUrl: (next?: string) =>
    `/auth/dev-login${next ? `?next=${encodeURIComponent(next)}` : ""}`,
  logout: () => request<{ status: string }>("/auth/logout", { method: "POST" }),

  // ---- Threads / chat ----
  listThreads: (opts: { archived?: boolean; project_id?: number | null; inbox?: boolean } = {}) => {
    const u = new URLSearchParams();
    u.set("archived", opts.archived ? "true" : "false");
    if (opts.inbox) u.set("inbox", "true");
    else if (opts.project_id != null) u.set("project_id", String(opts.project_id));
    return request<Thread[]>(`/api/threads?${u.toString()}`);
  },
  createThread: (opts: { title?: string; project_id?: number | null } = {}) =>
    request<Thread>("/api/threads", {
      method: "POST",
      body: JSON.stringify({ title: opts.title, project_id: opts.project_id ?? null }),
    }),
  renameThread: (id: number, title: string) =>
    request<Thread>(`/api/threads/${id}`, { method: "PATCH", body: JSON.stringify({ title }) }),
  archiveThread: (id: number) =>
    request<{ status: string }>(`/api/threads/${id}/archive`, { method: "POST" }),
  deleteThread: (id: number) =>
    request<{ status: string }>(`/api/threads/${id}`, { method: "DELETE" }),
  getMessages: (id: number) => request<Message[]>(`/api/threads/${id}/messages`),

  // ---- Tasks ----
  listTaskTemplates: () => request<TaskTemplate[]>("/api/tasks/templates"),

  // ---- Library ----
  listLibrary: (filters: { category?: string; q?: string } = {}) => {
    const u = new URLSearchParams();
    if (filters.category) u.set("category", filters.category);
    if (filters.q) u.set("q", filters.q);
    const qs = u.toString();
    return request<LibrarySource[]>(`/api/library${qs ? `?${qs}` : ""}`);
  },
  getLibrarySource: (id: number) => request<LibrarySource>(`/api/library/${id}`),
  getLibraryChunks: (id: number) =>
    request<
      { id: number; ordinal: number; text: string; char_start: number; char_end: number; token_count: number }[]
    >(`/api/library/${id}/chunks`),
  libraryFileUrl: (id: number) => `/api/library/${id}/file`,
  uploadLibrary: async (file: File, meta: {
    title?: string; authors?: string; year?: number; source_category?: string;
    license_note?: string; abby_private?: boolean;
  }) => {
    const fd = new FormData();
    fd.set("file", file);
    if (meta.title) fd.set("title", meta.title);
    if (meta.authors) fd.set("authors", meta.authors);
    if (meta.year != null) fd.set("year", String(meta.year));
    fd.set("source_category", meta.source_category ?? "abby_private");
    if (meta.license_note) fd.set("license_note", meta.license_note);
    fd.set("abby_private", String(meta.abby_private ?? true));
    const resp = await fetch("/api/library/upload", { method: "POST", body: fd, credentials: "include" });
    if (!resp.ok) throw new ApiError(resp.status, (await resp.json()).detail ?? resp.statusText);
    return (await resp.json()) as LibrarySource;
  },
  addLibraryByUrl: (payload: {
    url: string; title?: string; authors?: string; year?: number;
    source_category?: string; license_note?: string; abby_private?: boolean;
  }) => request<LibrarySource>("/api/library/add-by-url", {
    method: "POST",
    body: JSON.stringify(payload),
  }),
  deleteLibrarySource: (id: number) =>
    request<{ status: string }>(`/api/library/${id}`, { method: "DELETE" }),

  // Library categories
  listLibraryCategories: (includeArchived = true) =>
    request<LibraryCategory[]>(`/api/library/categories?include_archived=${includeArchived ? "true" : "false"}`),
  createLibraryCategory: (label: string) =>
    request<LibraryCategory>("/api/library/categories", {
      method: "POST",
      body: JSON.stringify({ label }),
    }),
  updateLibraryCategory: (id: number, fields: Partial<Pick<LibraryCategory, "label" | "archived" | "sort_order">>) =>
    request<LibraryCategory>(`/api/library/categories/${id}`, {
      method: "PATCH",
      body: JSON.stringify(fields),
    }),
  deleteLibraryCategory: (id: number) =>
    request<{ status: string }>(`/api/library/categories/${id}`, { method: "DELETE" }),

  // ---- Saved docs ----
  listDocs: () => request<(AttributedRecord & { id: number; title: string; created_at: number; updated_at: number })[]>("/api/docs"),
  getDoc: (id: number) =>
    request<AttributedRecord & { id: number; title: string; body_md: string; created_at: number; updated_at: number }>(`/api/docs/${id}`),
  saveDoc: (payload: { title: string; body_md: string; created_from?: object }) =>
    request<AttributedRecord & { id: number; title: string; body_md: string }>("/api/docs", {
      method: "POST",
      body: JSON.stringify(payload),
    }),

  // ---- Research agent ----
  researchFind: (query: string) =>
    request<AttributedRecord & { run_id: number; query: string; candidates: ResearchCandidate[] }>("/api/research/find", {
      method: "POST",
      body: JSON.stringify({ query }),
    }),
  researchIngest: (run_id: number, approved_indexes: number[], category = "methods_general") =>
    request<{ run_id: number; results: { url: string | null; ok: boolean; source_id?: number; title?: string; error?: string }[] }>(
      "/api/research/ingest-approved",
      { method: "POST", body: JSON.stringify({ run_id, approved_indexes, category }) },
    ),

  // ---- History (unified feed across chats / tasks / research) ----
  listHistory: (opts: {
    kind?: "chat" | "task" | "research";
    project_id?: number | null;
    inbox?: boolean;
    tag_id?: number | null;
  } = {}) => {
    const u = new URLSearchParams();
    if (opts.kind) u.set("kind", opts.kind);
    if (opts.inbox) u.set("inbox", "true");
    else if (opts.project_id != null) u.set("project_id", String(opts.project_id));
    if (opts.tag_id != null) u.set("tag_id", String(opts.tag_id));
    const qs = u.toString();
    return request<HistoryItem[]>(`/api/history${qs ? `?${qs}` : ""}`);
  },
  deleteTaskRun: (id: number) =>
    request<{ status: string }>(`/api/tasks/runs/${id}`, { method: "DELETE" }),
  deleteResearchRun: (id: number) =>
    request<{ status: string }>(`/api/research/runs/${id}`, { method: "DELETE" }),

  // ---- Full-text search ----
  search: (q: string, kind?: SearchKind) => {
    const u = new URLSearchParams({ q });
    if (kind) u.set("kind", kind);
    return request<SearchResponse>(`/api/search?${u.toString()}`);
  },

  // ---- Cost / usage ----
  usage: (days = 30) => request<UsageSummary>(`/api/usage?days=${days}`),

  // ---- Projects ----
  listProjects: (includeArchived = false) =>
    request<Project[]>(`/api/projects?include_archived=${includeArchived ? "true" : "false"}`),
  createProject: (name: string, color: string | null = null) =>
    request<Project>("/api/projects", { method: "POST", body: JSON.stringify({ name, color }) }),
  updateProject: (id: number, fields: Partial<Pick<Project, "name" | "color" | "archived" | "sort_order">>) =>
    request<Project>(`/api/projects/${id}`, { method: "PATCH", body: JSON.stringify(fields) }),
  deleteProject: (id: number) =>
    request<{ status: string }>(`/api/projects/${id}`, { method: "DELETE" }),
  setItemProject: (kind: ItemKind, ref_id: number, project_id: number | null) =>
    request<{ status: string }>(
      `/api/items/${kind}/${ref_id}/project`,
      { method: "PATCH", body: JSON.stringify({ project_id }) },
    ),

  // ---- Tags ----
  listTags: () => request<Tag[]>("/api/tags"),
  createTag: (name: string, color: string | null = null) =>
    request<Tag>("/api/tags", { method: "POST", body: JSON.stringify({ name, color }) }),
  updateTag: (id: number, fields: Partial<Pick<Tag, "name" | "color">>) =>
    request<Tag>(`/api/tags/${id}`, { method: "PATCH", body: JSON.stringify(fields) }),
  deleteTag: (id: number) =>
    request<{ status: string }>(`/api/tags/${id}`, { method: "DELETE" }),
  addItemTag: (kind: ItemKind, ref_id: number, tag_id: number) =>
    request<{ status: string }>(
      `/api/items/${kind}/${ref_id}/tags`,
      { method: "POST", body: JSON.stringify({ tag_id }) },
    ),
  removeItemTag: (kind: ItemKind, ref_id: number, tag_id: number) =>
    request<{ status: string }>(
      `/api/items/${kind}/${ref_id}/tags/${tag_id}`,
      { method: "DELETE" },
    ),
};

export interface UsageBucket {
  cost_usd: number;
  calls: number;
  input_tokens: number;
  output_tokens: number;
  provider?: string;
  feature?: string;
  model?: string;
}

export interface UsageEvent {
  id: number;
  user_id: number | null;
  provider: string;
  model: string;
  feature: string;
  input_tokens: number;
  output_tokens: number;
  cache_creation_tokens: number;
  cache_read_tokens: number;
  n_searches: number;
  cost_usd: number;
  created_at: number;
}

export interface UsageSummary {
  total_usd: number;
  event_count: number;
  by_provider: UsageBucket[];
  by_feature: UsageBucket[];
  by_model: UsageBucket[];
  daily: { day: string; cost_usd: number }[];
  recent: UsageEvent[];
}

export type SearchKind = "chat" | "task" | "research" | "doc" | "library";

export interface SearchResult {
  kind: SearchKind;
  ref_id: number;
  title: string;
  excerpt: string; // contains <mark>…</mark> tags around matches
  rank: number;
}

export interface SearchResponse {
  query: string;
  fts_query?: string;
  results: SearchResult[];
}

interface HistoryItemBase extends AttributedRecord {
  project_id: number | null;
  tags: { id: number; name: string; color: string | null }[];
}

export type HistoryItem =
  | (HistoryItemBase & {
      kind: "chat";
      id: number;
      title: string;
      preview: string;
      message_count: number;
      archived: boolean;
      created_at: number;
      updated_at: number;
      sort_key: number;
    })
  | (HistoryItemBase & {
      kind: "task";
      id: number;
      template_key: string;
      title: string;
      preview: string;
      inputs: Record<string, unknown>;
      result_text: string | null;
      result_doc_id: number | null;
      created_at: number;
      sort_key: number;
    })
  | (HistoryItemBase & {
      kind: "research";
      id: number;
      title: string;
      preview: string;
      candidates: ResearchCandidate[];
      candidate_count: number;
      approved_source_ids: number[];
      approved_count: number;
      created_at: number;
      sort_key: number;
    });
